import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import URR, { getAttachmentByID } from "./URR";
import "./teethControls.scss";
import TeethPosition from "./TeethPosition";
import AttachmentSelector, { attachmentTypes } from "./AttachmentSelector";
import IPRSelector from "./IPRSelector";
import ReviseTreatment from "../ReviseTreatment";
import { useGlobalStore } from "../../../store";

import { ShowOriginal } from "../ShowOriginal";
import {
  Controller,
  heatMapManager,
  attachmentMaterial as oldAttachmentMaterial,
  selectedTeethMaterial,
  teethMeshObject,
} from "../Mesh";
import {
  undoMovementList,
  redoMovementList,
  setRedoList,
  MovementTypes,
  toothMovementAmount,
  teethRotationAmount,
  roundVector,
} from "./TeethPositionHelper";
import {
  attachmentsObjects,
  attachmentMaterial,
  subscribeAttachmentToListener,
} from "./addRemoveAttachment";
import { rotateTooth } from "./TeethPositionHelper";
import { lockTooth } from "./TeethPositionHelper";

import { useQuery } from "@tanstack/react-query";
import { apiResolver, getDoctorConfig } from "@/helpers/apiResolver";
import moment from "moment";
import { hotjar } from "react-hotjar";
import { getParams } from "@/helpers/getParams";
import { removeListenersFrom } from "@/helpers/clickManager";
import SidePanel3DControls from "./Sidepanel3DControls";
import { isComparingMode } from "../modifications/ComparePlans";
import { globalBiteJump, isBJ } from "../Scene";
export let hasDoneOnce = false;
import { MathUtils } from "three";
import {
  prepareIPRs3DControls,
  updateIPRBetweenTeeth,
  iprOverLays as globalIprOverLays,
  setIprOverlays as setGlobalIprOverLays,
} from "@/helpers/ipr";
import { ShowContModification } from "../ShowContModification";
import { generateHeatmapsByName as generateHeatmapsByNames } from "../Heatmaps";

let showOriginalLength = 0;
let IPRCopyArray = [];
let group = {};

export function setHasDoneOnce(state) {
  if (globalBiteJump) {
    hasDoneOnce = state;
  }
}
const adjacentToothLength = 15;

export let isLeftShiftDown = false;

export function setIsLeftShiftDown(status) {
  isLeftShiftDown = status;
}

export function getAdjacentTeeth(tooth) {
  if (Array.isArray(tooth)) {
    tooth = tooth[0];
  }
  const currentIndex = tooth.parent.children.indexOf(tooth);
  let startIndex, endIndex;
  startIndex = MathUtils.clamp(
    currentIndex - adjacentToothLength,
    0,
    tooth.parent.children.length,
  );

  endIndex = MathUtils.clamp(
    currentIndex + adjacentToothLength,
    0,
    tooth.parent.children.length,
  );
  return [
    ...tooth.parent.children.slice(startIndex, currentIndex),
    ...tooth.parent.children.slice(currentIndex + 1, endIndex + 1),
  ];
}

export const prepareRedoListToExport = redoList => {
  for (let i = 0; i < redoList.length; i++) {
    if (Array.isArray(redoList[i])) {
      for (let t = 0; t < redoList[i].length; t++) {
        redoList[i][t].tooth = redoList[i][t]?.tooth.name;
      }
    } else {
      redoList[i].tooth = redoList[i].tooth.name;
    }
  }
  // renameKeys(redoList, objectAbbreviation);
  return redoList;
};
// #region redoList key shortening
const objectAbbreviation = {
  teethMovementRotationDirection: "TMRD",
  actionType: "AT",
  preAmount: "PA",
  isChunckMovement: "ICM",
  rotationPoint: "RP",
  degrees: "D",
  tooth: "T",
  action: "A",
  amount: "Am",
  from: "F",
  type: "Ty",
  movement: "M",
  attachmentID: "AID",
  attachmentName: "AN",
  relativePosition: "ReP",
  relativeRotation: "RR",
};

const objectAbbreviationReversed = {
  TMRD: "teethMovementRotationDirection",
  AT: "actionType",
  PA: "preAmount",
  ICM: "isChunckMovement",
  RP: "rotationPoint",
  D: "degrees",
  T: "tooth",
  A: "action",
  Am: "amount",
  F: "from",
  Ty: "type",
  M: "movement",
  AID: "attachmentID",
  AN: "attachmentName",
  ReP: "relativePosition",
  RR: "relativeRotation",
};
function renameKeys(redoList, originalToNew) {
  for (let index = 0; index < redoList.length; index++) {
    if (Array.isArray(redoList[index])) {
      for (let sIndex = 0; sIndex < redoList[index].length; sIndex++) {
        renameObject(redoList[index][sIndex], originalToNew);
      }
    } else {
      renameObject(redoList[index], originalToNew);
    }
  }
}

let keysHolder;
function renameObject(object, keysList) {
  keysHolder = Object.keys(object);
  const holder = keysList;
  for (let index = 0; index < keysHolder.length; index++) {
    if (holder[keysHolder[index]]) {
      object[holder[keysHolder[index]]] = object[keysHolder[index]];
      delete object[keysHolder[index]];
    }
  }
}
//#endregion

export const prepareRedoListToAutoSave = redoList => {
  const t = Date.now();
  if (Array.isArray(redoList[0])) {
    if (typeof redoList[0][0].tooth == "string") {
      return;
    }
    group.current = redoList[0][0].tooth.parent.parent;
  } else {
    group.current = redoList[0].tooth.parent.parent;
  }

  for (let i = 0; i < redoList.length; i++) {
    if (Array.isArray(redoList[i])) {
      for (let t = 0; t < redoList[i].length; t++) {
        if (!redoList[i][t]?.tooth.name) {
          continue;
        }
        redoList[i][t].tooth = redoList[i][t]?.tooth.name;
      }
    } else {
      redoList[i].tooth = redoList[i].tooth.name;
    }
  }
  const redoListCopy = JSON.parse(JSON.stringify(redoList));
  return redoListCopy;
};

const TeethControls = forwardRef(
  (
    {
      startRevision,
      dentalNotation,
      maxStepNumber,
      currentStep,
      tpLoaded,
      GLBData,
      iprOverLays,
      setIprOverlays,
      movementTable,
      parsedMovementTables,
      model,
    },
    ref,
  ) => {
    const demoTS = getParams().demoTS === "true" ? true : false;
    const { data } = demoTS
      ? true
      : useQuery({
          queryKey: ["treatmentSetupInfo"],
          queryFn: apiResolver,
          staleTime: 600000, // Cache data for 60 seconds (1 minute)
        });

    const withPatientInfo =
      getParams().withPatientInfo === "true" ? true : false;
    const isViewerControlsEnabled = data?.isViewerControlsEnabled;
    const {
      setControls3D,
      show3DControls,
      showContModification,
      setToggle,
      logs,
      setLogs,
      scene,
      attachmentArray,
      setLayout,
    } = useGlobalStore(state => ({
      show3DControls: state.toggle.show3DControls,
      showContModification: state.toggle.showContModification,
      setToggle: state.setToggle,
      logs: state.logs,
      setLogs: state.setLogs,
      scene: state.scene,
      attachmentArray: state.controls3D.attachmentArray,
      setControls3D: state.setControls3D,
      setLayout: state.setLayout,
    }));

    // Object.values(GLBData[0].lower?.gum).length > 0 &&
    // Object.values(GLBData[0].upper?.gum).length > 0;

    function reset(isShowOriginal = false, ignoreIPR = false) {
      const movedTeeth = new Set();
      if (!ignoreIPR) {
        Controller.detach();
      }
      let counter = 0;
      let newIprOverLays = JSON.parse(JSON.stringify(globalIprOverLays));
      while (undoMovementList.length !== 0) {
        let isArray = Array.isArray(
          undoMovementList[undoMovementList.length - 1],
        );

        const t = undoMovementList.pop();
        counter++;
        if (!isArray) {
          if (t.movement) {
            t.tooth.position.add(t.movement);
            movedTeeth.add(t.tooth.name);
            reflectToChangelog(t);
          } else if (t.degrees) {
            rotateTooth(
              t.rotationPoint,
              t.teethMovementRotationDirection,
              t.degrees,
              t.tooth,
            );
            movedTeeth.add(t.tooth.name);

            reflectToChangelog(t);
            // rotateAroundPoint(t.tooth, t.rotationPoint, t.teethMovementRotationDirection, t.degrees, true)
          } else if (t.actionType === MovementTypes.LOCK) {
            setControls3D("selectedTooth", t.tooth);
            lockTooth(true, t.tooth);
          } else if (t.actionType === MovementTypes.ATTACHMENTS) {
            if (t.action === "add") {
              for (let i = 0; i < t.tooth.children[0].children.length; i++) {
                if (
                  t.tooth.children[0].children[i].name == t.attachmentName &&
                  t.attachmentID === t.tooth.children[0].children[i].customID
                ) {
                  redoMovementList.push({
                    tooth: t.tooth,
                    action: t.action,
                    actionType: t.actionType,
                    attachmentName: t.tooth.children[0].children[i].name,
                    attachmentID: t.attachmentID,
                    relativePosition: roundVector(
                      t.tooth.children[0].children[i].position.clone(),
                    ),
                    relativeRotation: roundVector(
                      t.tooth.children[0].children[i].rotation.clone(),
                    ),
                  });

                  let selectedToothNum = t.tooth.name
                    .split("_")[2]
                    .substring(1, 4);
                  let action = "";
                  if (attachmentArray[selectedToothNum]?.length > 0) {
                    for (
                      let attachmentIndex = 0;
                      attachmentIndex <
                      attachmentArray[selectedToothNum].length;
                      attachmentIndex++
                    ) {
                      if (
                        t.attachmentID ===
                        attachmentArray[selectedToothNum][attachmentIndex]
                          .customID
                      ) {
                        action = "remove";
                        break;
                      }
                    }
                  }

                  let key = selectedToothNum.toString() + "_Movement";

                  if (!logs[key]) {
                    logs[key] = {};
                    logs[key][MovementTypes.MESIALDISTAL] = 0;
                    logs[key][MovementTypes.BUCCALLINGUAL] = 0;
                    logs[key][MovementTypes.EXTRUSIONINTRUSION] = 0;
                    logs[key][MovementTypes.ROTATION] = 0;
                    logs[key][MovementTypes.ANGULATION] = 0;
                    logs[key][MovementTypes.INCLINATION] = 0;
                    logs[key][MovementTypes.LEFTHINGE] = 0;
                    logs[key][MovementTypes.RIGHTHINGE] = 0;
                  }
                  if (!logs[key].hasOwnProperty("attachment")) {
                    logs[key]["attachment"] = {};
                  }
                  logs[key]["attachment"][t.attachmentID] = {
                    action: action,
                    type: t.tooth.children[0].children[i].name,
                  };

                  // if (logs[key]) {
                  //   const obj = Object.assign({}, logs[key]);
                  //   delete logs[key];
                  //   obj["attachment"][t.attachmentID] = {
                  //     action: action,
                  //     type: t.tooth.children[0].children[i].name,
                  //   };
                  //   logs[key] = obj;
                  // } else {
                  //   logs[key] = {};
                  //   if (!logs[key]["attachment"]) {
                  //     logs[key]["attachment"] = {};
                  //   }
                  //   logs[key]["attachment"][t.attachmentID] = {
                  //     action: action,
                  //     type: t.tooth.children[0].children[i].name,
                  //   };
                  // }
                  setLogs(logs);
                  removeListenersFrom(t.tooth.children[0].children[i]);
                  t.tooth.children[0].children[i].removeFromParent();
                }
              }
            } else if (t.action === "remove") {
              let attachment;
              for (let i = 0; i < attachmentsObjects.length; i++) {
                if (attachmentsObjects[i].name === t.attachmentName) {
                  attachment = attachmentsObjects[i].clone();
                }
              }

              let action;
              if (!attachment) {
                // old attachment
                const toothNumberKey = t.tooth.name.split("_")[2].substring(1);
                for (
                  let attachmentIndex = 0;
                  attachmentIndex < attachmentArray[toothNumberKey].length;
                  attachmentIndex++
                ) {
                  if (
                    attachmentArray[toothNumberKey][attachmentIndex].customID ==
                    t.attachmentID
                  ) {
                    attachment =
                      attachmentArray[toothNumberKey][attachmentIndex].clone();
                    attachment.name = "_att";
                    attachment.material = oldAttachmentMaterial;
                    break;
                  }
                }
                action = "";
              } else {
                action = "add";
                attachment.material = attachmentMaterial;
              }
              t.tooth.children[0].add(attachment);
              attachment.customID = t.attachmentID;

              subscribeAttachmentToListener(attachment);
              attachment.position.copy(t.relativePosition);
              attachment.rotation.copy(t.relativeRotation);
              attachment.visible = true;

              redoMovementList.push({
                tooth: t.tooth,
                action: t.action,
                actionType: t.actionType,
                attachmentName: t.attachmentName,
                attachmentID: t.attachmentID,
              });

              let selectedToothNum = t.tooth.name.split("_")[2].substring(1, 4);

              let key = selectedToothNum.toString() + "_Movement";

              if (!logs[key]) {
                logs[key] = {};
                logs[key][MovementTypes.MESIALDISTAL] = 0;
                logs[key][MovementTypes.BUCCALLINGUAL] = 0;
                logs[key][MovementTypes.EXTRUSIONINTRUSION] = 0;
                logs[key][MovementTypes.ROTATION] = 0;
                logs[key][MovementTypes.ANGULATION] = 0;
                logs[key][MovementTypes.INCLINATION] = 0;
                logs[key][MovementTypes.LEFTHINGE] = 0;
                logs[key][MovementTypes.RIGHTHINGE] = 0;
              }
              if (!logs[key].hasOwnProperty("attachment")) {
                logs[key]["attachment"] = {};
              }
              logs[key]["attachment"][t.attachmentID] = {
                action: action,
                type: attachment.name,
              };

              // if (logs[key]) {
              //   const obj = Object.assign({}, logs[key]);
              //   delete logs[key];
              //   obj["attachment"][t.attachmentID] = {
              //     action: action,
              //     type: attachment.name,
              //   };
              //   logs[key] = obj;
              // } else {
              //   logs[key] = {};
              //   if (!logs[key]["attachment"]) {
              //     logs[key]["attachment"] = {};
              //   }
              //   logs[key]["attachment"][t.attachmentID] = {
              //     action: action,
              //     type: attachment.name,
              //   };
              // }
              setLogs(logs);
            } else if (t.action === "move") {
              for (let i = 0; i < t.tooth.children[0].children.length; i++) {
                if (t.tooth.children[0].children[i].name.includes("att")) {
                  redoMovementList.push({
                    tooth: t.tooth,
                    action: t.action,
                    actionType: t.actionType,
                    attachmentName: t.tooth.children[0].children[i].name,
                    relativePosition: roundVector(
                      t.tooth.children[0].children[i].position.clone(),
                    ),
                    relativeRotation: roundVector(
                      t.tooth.children[0].children[i].rotation.clone(),
                    ),
                  });
                  removeListenersFrom(t.tooth.children[0].children[i]);

                  t.tooth.children[0].children[i].removeFromParent();
                }

                let attachment;
                let isExistingAttachment = false;
                for (let i = 0; i < attachmentsObjects.length; i++) {
                  if (attachmentsObjects[i].name === t.attachmentName) {
                    attachment = attachmentsObjects[i].clone();
                  }
                }
                if (!attachment) {
                  // old attachment
                  isExistingAttachment = true;
                  attachment =
                    attachmentArray[
                      t.tooth.name.split("_")[2].substring(1)
                    ][0].clone();
                  attachment.material = oldAttachmentMaterial;
                } else {
                  attachment.material = attachmentMaterial;
                }
                t.tooth.children[0].add(attachment);
                subscribeAttachmentToListener(attachment);

                attachment.position.copy(t.relativePosition);
                attachment.rotation.copy(t.relativeRotation);
                attachment.visible = true;

                let selectedToothNum = t.tooth.name
                  .split("_")[2]
                  .substring(1, 4);
                let action;
                if (attachmentArray[selectedToothNum]?.length > 0) {
                  if (isExistingAttachment) {
                    action = "";
                  } else {
                    action = "replace";
                  }
                } else {
                  action = "add";
                }

                let key = selectedToothNum.toString() + "_Movement";
                if (logs[key]) {
                  const obj = Object.assign({}, logs[key]);
                  delete logs[key];
                  obj["attachment"] = {
                    action: action,
                    type: attachment.name,
                  };
                  logs[key] = obj;
                } else {
                  logs[key] = {};
                  logs[key]["attachment"] = {
                    action: action,
                    type: attachment.name,
                  };
                }
                setLogs(logs);
              }
            } else if (t.action === "rotate") {
              for (let i = 0; i < t.tooth.children[0].children.length; i++) {
                if (
                  t.tooth.children[0].children[i].name == t.attachmentName &&
                  t.attachmentID === t.tooth.children[0].children[i].customID
                ) {
                  redoMovementList.push({
                    tooth: t.tooth,
                    action: t.action,
                    actionType: t.actionType,
                    attachmentID: t.attachmentID,
                    attachmentName: t.tooth.children[0].children[i].name,
                    rotation: t.tooth.children[0].children[i].rotation.clone(),
                    continue: t.continue,
                  });
                  t.tooth.children[0].children[i].rotation.copy(t.rotation);
                  break;
                }
              }
            }
          }
          if (t.actionType !== MovementTypes.ATTACHMENTS) {
            redoMovementList.push(t);
          }
        } else {
          let len = t.length - 1;
          redoMovementList.push([]);
          let r;
          for (let i = 0; i <= len; i++) {
            r = t[i];
            if (r.movement) {
              r.tooth.position.add(r.movement);
              reflectToChangelog(r);
              movedTeeth.add(r.tooth.name);
            } else if (r.degrees) {
              rotateTooth(
                r.rotationPoint,
                r.teethMovementRotationDirection,
                r.degrees,
                r.tooth,
              );
              movedTeeth.add(r.tooth.name);
              reflectToChangelog(r);
              // rotateAroundPoint(t.tooth, t.rotationPoint, t.teethMovementRotationDirection, t.degrees, true)
            } else if (r.actionType === MovementTypes.LOCK) {
              setControls3D("selectedTooth", r.tooth);
              lockTooth(true, r.tooth);
            } else if (r.actionType === MovementTypes.IPR) {
              if (r.from < 17) {
                for (
                  let i = 0;
                  i < newIprOverLays[0]["t" + r.from].length;
                  i++
                ) {
                  if (
                    newIprOverLays[0]["t" + r.from][i].step ===
                    newIprOverLays[0]["t" + r.from].length - 1
                  ) {
                    newIprOverLays[0]["t" + r.from][i].amount =
                      newIprOverLays[0]["t" + r.from][i].amount -
                      (r.amount - r.preAmount);
                    break;
                  }
                }
              } else {
                for (
                  let i = 0;
                  i < newIprOverLays[1]["t" + r.from].length;
                  i++
                ) {
                  if (
                    newIprOverLays[1]["t" + r.from][i].step ===
                    newIprOverLays[1]["t" + r.from].length - 1
                  ) {
                    newIprOverLays[1]["t" + r.from][i].amount =
                      newIprOverLays[1]["t" + r.from][i].amount -
                      (r.amount - r.preAmount);
                    break;
                  }
                }
              }
              reflectToChangelog(r);
            }
            redoMovementList[redoMovementList.length - 1].push(r);
          }
        }
      }
      if (isShowOriginal) {
        showOriginalLength = counter;
        IPRCopyArray = newIprOverLays;
      } else {
        isShowOriginal = 0;
        if (heatMapManager) {
          generateHeatmapsByNames(movedTeeth);
        }
      }
      if (!ignoreIPR) {
        setIprOverlays(newIprOverLays);
        setGlobalIprOverLays(newIprOverLays);
        prepareIPRs3DControls(null, newIprOverLays);
      }
    }
    function redo(doesReturn = false, old, ignoreIPR = false) {
      let newIprOverLays;
      const movedTeeth = new Set();
      if (old) {
        newIprOverLays = old;
        // newArray = JSON.parse(JSON.stringify(old));
      } else {
        newIprOverLays = JSON.parse(JSON.stringify(globalIprOverLays));
      }
      let isArray = false;
      if (redoMovementList.length > 0) {
        isArray = Array.isArray(redoMovementList[redoMovementList.length - 1]);
      }
      let movementType, toothNum, arrLen, attachmentID;
      if (!isArray) {
        movementType =
          redoMovementList[redoMovementList.length - 1]?.actionType;

        toothNum = redoMovementList[redoMovementList.length - 1]?.tooth.name
          .split("_")[2]
          .substring(1, 4);
      } else {
        arrLen = redoMovementList[redoMovementList.length - 1].length - 1;
        movementType =
          redoMovementList[redoMovementList.length - 1][arrLen].actionType;
        toothNum = redoMovementList[redoMovementList.length - 1][
          arrLen
        ].tooth.name
          .split("_")[2]
          .substring(1, 4);
      }
      let attContinue = true;
      while (true) {
        if (!isArray) {
          if (redoMovementList.length === 0) {
            showOriginalLength = -1;
            return;
          }
          if (
            redoMovementList[redoMovementList.length - 1].actionType !==
            MovementTypes.ATTACHMENTS
          ) {
            if (
              movementType !==
                redoMovementList[redoMovementList.length - 1].actionType ||
              toothNum !==
                redoMovementList[redoMovementList.length - 1].tooth.name
                  .split("_")[2]
                  .substring(1, 4)
            ) {
              break;
            }
          } else {
            if (!attContinue) {
              break;
            }
          }
          const t = redoMovementList.pop();

          showOriginalLength--;
          if (t.movement) {
            t.tooth.position.sub(t.movement);
            movedTeeth.add(t.tooth.name);
            reflectToChangelog(t, -1);
          } else if (t.degrees) {
            rotateTooth(
              t.rotationPoint,
              t.teethMovementRotationDirection,
              -t.degrees,
              t.tooth,
              null,
              isComparingRedo,
            );
            movedTeeth.add(t.tooth.name);

            reflectToChangelog(t, -1);
            // rotateAroundPoint(t.tooth, t.rotationPoint, t.teethMovementRotationDirection, t.degrees, true)
          } else if (t.actionType === MovementTypes.ATTACHMENTS) {
            if (t.action === "add") {
              let attachment;
              for (let i = 0; i < attachmentsObjects.length; i++) {
                if (attachmentsObjects[i].name === t.attachmentName) {
                  attachment = attachmentsObjects[i].clone();
                }
              }

              t.tooth.children[0].add(attachment);
              attachment.customID = t.attachmentID;
              if (!isComparingRedo) {
                subscribeAttachmentToListener(attachment);
              }
              attachment.position.copy(t.relativePosition);
              attachment.rotation.copy(t.relativeRotation);

              attachment.visible = true;
              attachment.material = attachmentMaterial;
              undoMovementList.push({
                tooth: t.tooth,
                action: t.action,
                actionType: t.actionType,
                attachmentID: t.attachmentID,
                attachmentName: t.attachmentName,
                continue: t.continue,
              });

              let selectedToothNum = t.tooth.name.split("_")[2].substring(1, 4);
              let action;
              if (attachmentArray[selectedToothNum]?.length > 0) {
                action = "add";
              } else {
                action = "add";
              }

              let key = selectedToothNum.toString() + "_Movement";
              if (!logs[key]) {
                logs[key] = {};
                logs[key][MovementTypes.MESIALDISTAL] = 0;
                logs[key][MovementTypes.BUCCALLINGUAL] = 0;
                logs[key][MovementTypes.EXTRUSIONINTRUSION] = 0;
                logs[key][MovementTypes.ROTATION] = 0;
                logs[key][MovementTypes.ANGULATION] = 0;
                logs[key][MovementTypes.INCLINATION] = 0;
                logs[key][MovementTypes.LEFTHINGE] = 0;
                logs[key][MovementTypes.RIGHTHINGE] = 0;
              }
              if (!logs[key].hasOwnProperty("attachment")) {
                logs[key]["attachment"] = {};
              }
              logs[key]["attachment"][t.attachmentID] = {
                action: action,
                type: attachment.name,
              };

              // if (logs[key]) {
              //   const obj = Object.assign({}, logs[key]);
              //   delete logs[key];
              //   obj["attachment"][t.attachmentID] = {
              //     action: action,
              //     type: attachment.name,
              //   };
              //   logs[key] = obj;
              // } else {
              //   logs[key] = {};
              //   if (!logs[key]["attachment"]) {
              //     logs[key]["attachment"] = {};
              //   }
              //   logs[key]["attachment"][t.attachmentID] = {
              //     action: action,
              //     type: attachment.name,
              //   };
              // }
              setLogs(logs);
            } else if (t.action === "remove") {
              if (!isComparingRedo) {
                for (let i = 0; i < t.tooth.children[0].children.length; i++) {
                  if (
                    t.tooth.children[0].children[i].name == t.attachmentName &&
                    t.tooth.children[0].children[i].customID === t.attachmentID
                  ) {
                    attachmentID = t.attachmentID;
                    undoMovementList.push({
                      tooth: t.tooth,
                      action: t.action,
                      actionType: t.actionType,
                      attachmentID: attachmentID,
                      attachmentName: t.tooth.children[0].children[i].name,
                      continue: t.continue,
                      relativePosition: roundVector(
                        t.tooth.children[0].children[i].position.clone(),
                      ),
                      relativeRotation: roundVector(
                        t.tooth.children[0].children[i].rotation.clone(),
                      ),
                    });

                    let selectedToothNum = t.tooth.name
                      .split("_")[2]
                      .substring(1, 4);
                    let action;
                    if (
                      t.tooth.children[0].children[i].name == "_att" &&
                      attachmentArray[selectedToothNum]?.length > 0
                    ) {
                      action = "remove";
                    } else {
                      action = "";
                    }

                    let key = selectedToothNum.toString() + "_Movement";

                    if (!logs[key]) {
                      logs[key] = {};
                      logs[key][MovementTypes.MESIALDISTAL] = 0;
                      logs[key][MovementTypes.BUCCALLINGUAL] = 0;
                      logs[key][MovementTypes.EXTRUSIONINTRUSION] = 0;
                      logs[key][MovementTypes.ROTATION] = 0;
                      logs[key][MovementTypes.ANGULATION] = 0;
                      logs[key][MovementTypes.INCLINATION] = 0;
                      logs[key][MovementTypes.LEFTHINGE] = 0;
                      logs[key][MovementTypes.RIGHTHINGE] = 0;
                    }
                    if (!logs[key].hasOwnProperty("attachment")) {
                      logs[key]["attachment"] = {};
                    }
                    logs[key]["attachment"][attachmentID] = {
                      action: action,
                      type: t.tooth.children[0].children[i].name,
                    };

                    // if (logs[key]) {
                    //   const obj = Object.assign({}, logs[key]);
                    //   delete logs[key];
                    //   obj["attachment"][attachmentID] = {
                    //     action: action,
                    //     type: t.tooth.children[0].children[i].name,
                    //   };
                    //   logs[key] = obj;
                    // } else {
                    //   logs[key] = {};
                    //   if (!logs[key]["attachment"]) {
                    //     logs[key]["attachment"] = {};
                    //   }

                    //   logs[key]["attachment"][attachmentID] = {
                    //     action: action,
                    //     type: t.tooth.children[0].children[i].name,
                    //   };
                    // }
                    setLogs(logs);
                    removeListenersFrom(t.tooth.children[0].children[i]);
                    t.tooth.children[0].children[i].removeFromParent();
                  }
                }
              } else {
                for (let i = 0; i < t.tooth.children[0].children.length; i++) {
                  t.tooth.children[0].children[i].removeFromParent();
                }
              }
            } else if (t.action === "move") {
              if (!isComparingRedo) {
                for (let i = 0; i < t.tooth.children[0].children.length; i++) {
                  if (
                    t.tooth.children[0].children[i].name == t.attachmentName
                  ) {
                    undoMovementList.push({
                      tooth: t.tooth,
                      action: t.action,
                      actionType: t.actionType,
                      attachmentName: t.tooth.children[0].children[i].name,
                      relativePosition: roundVector(
                        t.tooth.children[0].children[i].position.clone(),
                      ),
                      relativeRotation: roundVector(
                        t.tooth.children[0].children[i].rotation.clone(),
                      ),
                    });
                    removeListenersFrom(t.tooth.children[0].children[i]);

                    t.tooth.children[0].children[i].removeFromParent();
                  }

                  let attachment;
                  let isExistingAttachment = false;

                  for (let i = 0; i < attachmentsObjects.length; i++) {
                    if (attachmentsObjects[i].name === t.attachmentName) {
                      attachment = attachmentsObjects[i].clone();
                    }
                  }
                  if (!attachment) {
                    // old attachment
                    isExistingAttachment = true;
                    attachment =
                      attachmentArray[
                        t.tooth.name.split("_")[2].substring(1)
                      ][0].clone();
                    attachment.material = oldAttachmentMaterial;
                  } else {
                    attachment.material = attachmentMaterial;
                  }
                  t.tooth.children[0].add(attachment);

                  subscribeAttachmentToListener(attachment);

                  attachment.position.copy(t.relativePosition);
                  attachment.rotation.copy(t.relativeRotation);
                  attachment.visible = true;

                  let selectedToothNum = t.tooth.name
                    .split("_")[2]
                    .substring(1, 4);
                  let action;
                  if (attachmentArray[selectedToothNum]?.length > 0) {
                    if (isExistingAttachment) {
                      action = "";
                    } else {
                      action = "add";
                    }
                  } else {
                    action = "add";
                  }

                  let key = selectedToothNum.toString() + "_Movement";
                  if (logs[key]) {
                    const obj = Object.assign({}, logs[key]);
                    delete logs[key];
                    obj["attachment"] = {
                      action: action,
                      type: attachment.name,
                    };
                    logs[key] = obj;
                  } else {
                    logs[key] = {};
                    logs[key]["attachment"] = {
                      action: action,
                      type: attachment.name,
                    };
                  }
                  setLogs(logs);
                }
              } else {
                for (let i = 0; i < t.tooth.children.length; i++) {
                  t.tooth.children[i].removeFromParent();
                }
                let attachment;
                let isExistingAttachment = false;

                for (let i = 0; i < attachmentsObjects.length; i++) {
                  if (attachmentsObjects[i].name === t.attachmentName) {
                    attachment = attachmentsObjects[i].clone();
                  }
                }
                if (!attachment) {
                  // old attachment
                  isExistingAttachment = true;
                  attachment =
                    attachmentArray[
                      t.tooth.name.split("_")[2].substring(1)
                    ][0].clone();
                  attachment.material = oldAttachmentMaterial;
                } else {
                  attachment.material = attachmentMaterial;
                }
                t.tooth.add(attachment);
                attachment.position.copy(t.relativePosition);
                attachment.rotation.copy(t.relativeRotation);
                attachment.visible = true;
              }
            } else if (t.action === "rotate") {
              for (let i = 0; i < t.tooth.children[0].children.length; i++) {
                if (
                  t.tooth.children[0].children[i].name == t.attachmentName &&
                  t.tooth.children[0].children[i].customID === t.attachmentID
                ) {
                  attachmentID = t.attachmentID;
                  undoMovementList.push({
                    tooth: t.tooth,
                    action: t.action,
                    actionType: t.actionType,
                    attachmentID: attachmentID,
                    attachmentName: t.tooth.children[0].children[i].name,
                    continue: t.continue,
                    rotation: t.tooth.children[0].children[i].rotation.clone(),
                  });
                  t.tooth.children[0].children[i].rotation.copy(t.rotation);
                  if (
                    redoMovementList.length === 0 &&
                    ignoreIPR &&
                    Controller.object
                  ) {
                    Controller.attach(t.tooth.children[0].children[i]);
                  }
                }
              }
            }
            if (doesReturn) {
              return newIprOverLays;
            } else {
              attContinue =
                redoMovementList[redoMovementList.length - 1]?.continue;
              continue;
            }
          } else if (t.actionType === MovementTypes.LOCK) {
            setControls3D("selectedTooth", t.tooth);
            lockTooth(true, t.tooth);
          }
          undoMovementList.push(t);
          break;
        } else {
          let len = redoMovementList[redoMovementList.length - 1]?.length - 1;

          if (
            redoMovementList.length === 0 ||
            ((movementType !==
              redoMovementList[redoMovementList.length - 1][len]?.actionType ||
              (toothNum !==
                redoMovementList[redoMovementList.length - 1][len]?.tooth.name
                  .split("_")[2]
                  .substring(1, 4) &&
                !redoMovementList[redoMovementList.length - 1][len]
                  ?.isChunckMovement)) &&
              !redoMovementList[redoMovementList.length - 1][len]?.RM)
          ) {
            break;
          }
          const r = redoMovementList.pop();
          showOriginalLength--;
          undoMovementList.push([]);
          let t;
          for (let i = 0; i <= len; i++) {
            t = r[i];

            if (t.movement) {
              t.tooth.position.sub(t.movement);
              movedTeeth.add(t.tooth.name);
              reflectToChangelog(t, -1);
            } else if (t.degrees) {
              rotateTooth(
                t.rotationPoint,
                t.teethMovementRotationDirection,
                -t.degrees,
                t.tooth,
                null,
                isComparingRedo,
              );
              movedTeeth.add(t.tooth.name);
              reflectToChangelog(t, -1);
              // rotateAroundPoint(t.tooth, t.rotationPoint, t.teethMovementRotationDirection, t.degrees, true)
            } else if (t.actionType === MovementTypes.LOCK) {
              setControls3D("selectedTooth", t.tooth);
              lockTooth(true, t.tooth);
            } else if (t.actionType === MovementTypes.IPR && !isComparingRedo) {
              if (t.from < 17) {
                for (
                  let i = 0;
                  i < newIprOverLays[0]["t" + t.from].length;
                  i++
                ) {
                  if (
                    newIprOverLays[0]["t" + t.from][i].step ===
                    newIprOverLays[0]["t" + t.from].length - 1
                  ) {
                    newIprOverLays[0]["t" + t.from][i].amount =
                      newIprOverLays[0]["t" + t.from][i].amount +
                      (t.amount - t.preAmount);
                    break;
                  }
                }
              } else {
                for (
                  let i = 0;
                  i < newIprOverLays[1]["t" + t.from].length;
                  i++
                ) {
                  if (
                    newIprOverLays[1]["t" + t.from][i].step ===
                    newIprOverLays[1]["t" + t.from].length - 1
                  ) {
                    newIprOverLays[1]["t" + t.from][i].amount =
                      newIprOverLays[1]["t" + t.from][i].amount +
                      (t.amount - t.preAmount);
                    break;
                  }
                }
              }
              reflectToChangelog(t, -1);
            }
            undoMovementList[undoMovementList.length - 1].push(t);
          }
        }
        if (movementType === "attachments") {
          break;
        }
      }
      if (!ignoreIPR) {
        setIprOverlays(newIprOverLays);
        setGlobalIprOverLays(newIprOverLays);
        prepareIPRs3DControls(null, newIprOverLays);
      }
      if (doesReturn) {
        return newIprOverLays;
      } else if (heatMapManager) {
        generateHeatmapsByNames(movedTeeth);
      }
    }

    const setAutoSave = () => {
      const startTime = Date.now();
      if (demoTS) return;
      const t = Date.now();
      let r = [];
      if (undoMovementList.length !== 0) {
        reset(true, true);
        r = prepareRedoListToAutoSave(redoMovementList);
      }

      setControls3D("saving", true);
      localStorage.setItem(
        getParams().treatmentSetupsId + "_TP",
        JSON.stringify({
          redo: r,
          date: moment().format(),
        }),
      );

      if (r.length !== 0) {
        prepareRedoListForAutoSave(redoMovementList, group);

        showOriginalRemovedHover(true);
      }
      setControls3D("autoSaveDate", moment().format());
      setTimeout(() => {
        setControls3D("saved", false);
      }, 300);
      setTimeout(() => {
        setControls3D("saving", false);
        setControls3D("saved", true);
      }, 500);
    };
    const clearAutoSave = () => {
      localStorage.removeItem(getParams().treatmentSetupsId + "_TP");
      setControls3D("saving", false);
      setControls3D("saved", false);
    };

    function showOriginalRemovedHover(ignoreIPR = false) {
      let old;
      const startTime = Date.now();

      if (showOriginalLength > 0) {
        old = IPRCopyArray;
      }
      //console.log(showOriginalLength, "the length is");
      while (showOriginalLength > 0) {
        if (old) {
          old = redo(true, old, true);
        } else {
          old = redo(true, undefined, true);
        }
      }
      if (!ignoreIPR && old) {
        setIprOverlays(old);
        prepareIPRs3DControls(null, old);
      }
    }

    function redoAll() {
      let newIprOverLays;
      prepareIPROverlaysLastStep();
      while (redoMovementList.length > 0) {
        if (newIprOverLays) {
          newIprOverLays = redo(true, newIprOverLays, true);
        } else {
          newIprOverLays = redo(true, undefined, true);
        }
      }
      setIprOverlays(newIprOverLays);
      prepareIPRs3DControls(null, newIprOverLays);
    }

    function prepareIPROverlaysLastStep() {
      let toothKeyCacher;
      for (let isLower = 0; isLower < 2; isLower++) {
        for (let toothNumber = 1; toothNumber < 33; toothNumber++) {
          toothKeyCacher = "t" + toothNumber;
          if (iprOverLays[isLower].hasOwnProperty(toothKeyCacher)) {
            for (
              let step = 0;
              step < iprOverLays[isLower][toothKeyCacher].length;
              step++
            ) {
              if (
                iprOverLays[isLower][toothKeyCacher][step].step ===
                  iprOverLays[isLower][toothKeyCacher].length - 1 ||
                iprOverLays[isLower][toothKeyCacher][step].amount === null
              ) {
                iprOverLays[isLower][toothKeyCacher][step].step =
                  iprOverLays[isLower][toothKeyCacher].length - 1;
                break;
              }
            }
          }
        }
      }
    }

    let isComparingRedo = false;
    /**
     * @param {List} redoList
     * @param {List} group a group in the shape of the main gums group
     */
    //useImperativeHandle(ref, () => ({increment}))
    useImperativeHandle(
      ref,
      () => ({ redoAllComparingMode, applyAutoSave, setAutoSave }),
      [],
    );

    function redoAllComparingMode(_redoList, group) {
      if (!_redoList || !isComparingMode || hasDoneOnce) {
        return;
      }
      const redoList = JSON.parse(JSON.stringify(_redoList));
      const listHolder = JSON.parse(JSON.stringify(redoMovementList));
      prepareRedoListToImport(redoList, group);
      isComparingRedo = true;
      let old;
      setRedoList(redoList);
      while (redoMovementList.length > 0) {
        if (old) {
          old = redo(true, old);
        } else {
          old = redo(true, undefined);
        }
      }
      setRedoList(listHolder);
      isComparingRedo = false;
      hasDoneOnce = true;
    }
    const applyAutoSave = (redoList, group) => {
      // prepareRedoListForAutoSave(redoList, group);
      if (!redoList) {
        return;
      }
      prepareRedoListForAutoSave(redoList, group);
      isComparingRedo = false;
      setRedoList(redoList);
      if (redoMovementList.length > 0) {
        redoAll();
      }
      isComparingRedo = false;
    };

    function prepareRedoListToImport(redoList, group) {
      for (let i = 0; i < redoList.length; i++) {
        if (Array.isArray(redoList[i])) {
          for (let t = 0; t < redoList[i].length; t++) {
            redoList[i][t].tooth = getToothByName(redoList[i][t].tooth, group);
          }
        } else {
          redoList[i].tooth = getToothByName(redoList[i].tooth, group);
        }
      }
    }
    function prepareRedoListForAutoSave(redoList, group) {
      const startTime = Date.now();

      for (let i = 0; i < redoList.length; i++) {
        if (Array.isArray(redoList[i])) {
          for (let t = 0; t < redoList[i].length; t++) {
            redoList[i][t].tooth = getToothByNameAutoSave(
              redoList[i][t].tooth,
              group,
            );
          }
        } else {
          redoList[i].tooth = getToothByNameAutoSave(redoList[i].tooth, group);
        }
      }
    }
    function getToothByName(tooth, theGroup) {
      const toothNumber = Number(tooth.split("_")[2].substring(1, 4));
      const isLower = toothNumber < 17 ? 0 : 1;
      const maxStepNumber = Math.ceil(theGroup.current.children.length / 2 - 1);
      const toothParent =
        theGroup.current.children[maxStepNumber * 2 + isLower];
      const toothAlter = toothParent.children.find(Element => {
        if (Element.name === tooth) return Element;
      });
      return toothAlter;
    }
    function getToothByNameAutoSave(tooth, theGroup) {
      // let cacher = teethMeshObject[tooth];
      // return cacher;
      const toothNumber = Number(tooth.split("_")[2].substring(1, 4));
      const isLower = toothNumber < 17 ? 0 : 1;
      let maxStepNumber = 0;
      theGroup.current.children.forEach((elem, i) => {
        if (!elem.name.includes("gum")) {
          return;
        }
        maxStepNumber = Math.ceil((i + 1) / 2 - 1);
      });
      const toothParent =
        theGroup.current.children[maxStepNumber * 2 + isLower];
      const toothAlter = toothParent.children.find(Element => {
        if (Element.name === tooth) return Element;
      });
      if (!toothAlter) {
        console.log("failed finding a match to ", tooth);
      }
      return toothAlter;
    }

    function reflectToChangelog(theMovement, isUndo = 1) {
      const localTeethMovementAmount = theMovement.TMA
        ? theMovement.TMA
        : toothMovementAmount;
      if (isComparingRedo) {
        return;
      }

      let toothNum;
      if (theMovement.tooth) {
        toothNum = theMovement.tooth.name.split("_")[2].substring(1, 4);
      }
      if (!logs[toothNum + "_Movement"]) {
        logs[toothNum + "_Movement"] = {};
        logs[toothNum + "_Movement"][MovementTypes.MESIALDISTAL] = 0;
        logs[toothNum + "_Movement"][MovementTypes.BUCCALLINGUAL] = 0;
        logs[toothNum + "_Movement"][MovementTypes.EXTRUSIONINTRUSION] = 0;
        logs[toothNum + "_Movement"][MovementTypes.ROTATION] = 0;
        logs[toothNum + "_Movement"][MovementTypes.ANGULATION] = 0;
        logs[toothNum + "_Movement"][MovementTypes.INCLINATION] = 0;
        logs[toothNum + "_Movement"][MovementTypes.LEFTHINGE] = 0;
        logs[toothNum + "_Movement"][MovementTypes.RIGHTHINGE] = 0;
      }
      switch (theMovement.actionType) {
        case MovementTypes.EXTRUSIONINTRUSION: {
          let factor = 1;
          if (theMovement.movement.y < 0) {
            factor = -1;
          }
          logs[toothNum + "_Movement"][MovementTypes.EXTRUSIONINTRUSION] =
            Math.round(
              (logs[toothNum + "_Movement"][MovementTypes.EXTRUSIONINTRUSION] +
                toothMovementAmount * factor * isUndo) *
                100,
            ) / 100;
          break;
        }
        case MovementTypes.MESIALDISTAL: {
          let factor = 1;
          if (theMovement.type === "Mesial") {
            factor = -1;
          }
          //console.log(factor);

          logs[toothNum + "_Movement"][MovementTypes.MESIALDISTAL] =
            Math.round(
              (logs[toothNum + "_Movement"][MovementTypes.MESIALDISTAL] +
                localTeethMovementAmount * factor * isUndo) *
                100,
            ) / 100;
          break;
        }
        case MovementTypes.BUCCALLINGUAL: {
          let factor = 1;
          if (theMovement.type === "Lingual") {
            factor = -1;
          }
          logs[toothNum + "_Movement"][MovementTypes.BUCCALLINGUAL] =
            Math.round(
              (logs[toothNum + "_Movement"][MovementTypes.BUCCALLINGUAL] +
                localTeethMovementAmount * factor * isUndo) *
                100,
            ) / 100;
          break;
        }
        case MovementTypes.ROTATION: {
          let factor = 1;
          if (theMovement.degrees < 0) {
            factor = -1;
          }
          logs[toothNum + "_Movement"][MovementTypes.ROTATION] =
            Math.round(
              (logs[toothNum + "_Movement"][MovementTypes.ROTATION] +
                teethRotationAmount * factor * isUndo) *
                100,
            ) / 100;
          break;
        }
        case MovementTypes.ANGULATION: {
          let factor = 1;
          if (theMovement.degrees < 0) {
            factor = -1;
          }
          logs[toothNum + "_Movement"][MovementTypes.ANGULATION] =
            Math.round(
              (logs[toothNum + "_Movement"][MovementTypes.ANGULATION] +
                teethRotationAmount * factor * isUndo) *
                100,
            ) / 100;
          break;
        }
        case MovementTypes.INCLINATION: {
          let factor = 1;
          if (theMovement.degrees < 0) {
            factor = -1;
          }
          logs[toothNum + "_Movement"][MovementTypes.INCLINATION] =
            Math.round(
              (logs[toothNum + "_Movement"][MovementTypes.INCLINATION] +
                teethRotationAmount * factor * isUndo) *
                100,
            ) / 100;
          break;
        }
        case MovementTypes.LEFTHINGE: {
          let factor = 1;
          if (theMovement.degrees < 0) {
            factor = -1;
          }
          logs[toothNum + "_Movement"][MovementTypes.LEFTHINGE] =
            Math.round(
              (logs[toothNum + "_Movement"][MovementTypes.LEFTHINGE] +
                teethRotationAmount * factor * isUndo) *
                100,
            ) / 100;
          break;
        }
        case MovementTypes.RIGHTHINGE: {
          let factor = 1;
          if (theMovement.degrees < 0) {
            factor = -1;
          }
          logs[toothNum + "_Movement"][MovementTypes.RIGHTHINGE] =
            Math.round(
              (logs[toothNum + "_Movement"][MovementTypes.RIGHTHINGE] +
                teethRotationAmount * factor * isUndo) *
                100,
            ) / 100;
          break;
        }
        case MovementTypes.IPR: {
          const name = theMovement.from + "_IPR";
          if (!logs[name]) {
            logs[name] = {};
            logs[name].action = theMovement.action;
            logs[name].amount = theMovement.amount;
            logs[name].preAmount = theMovement.preAmount;
            logs[name].from = theMovement.from;
            logs[name].to = theMovement.to;
            logs[name].type = theMovement.type;
          } else {
            logs[name].amount =
              (logs[name].amount ? logs[name].amount : 0) +
              isUndo * (theMovement.preAmount - theMovement.amount);
          }
          break;
        }
        case MovementTypes.ATTACHMENTS: {
          if (theMovement.action === "added" && isUndo !== 1) {
            logs[toothNum + "_Movement"]["attachment"] = {
              action: theMovement.action,
              type: theMovement.attachment,
            };
          } else {
            delete logs[toothNum + "_Movement"]["attachment"];
          }
          break;
        }
      }
      setLogs(logs);
      // console.log(logs);
    }
    function startTracking() {
      // hotjar tracking
      window.hj("event", "3d_controls");
      hotjar.event("3d_controls");

      // end hotjar tracking
    }
    return startRevision ? (
      <div
        onPointerEnter={() => {
          setLayout("isUI", true);
        }}
        onMouseLeave={() => {
          setLayout("isUI", false);
        }}
        className={`absolute right-0 ${
          withPatientInfo ? "top-[90px]" : demoTS ? "top-[120px]" : "top-[60px]"
        } z-10 flex ${
          withPatientInfo
            ? "h-[calc(100vh-100px)]"
            : demoTS
              ? "h-[calc(100vh-130px)]"
              : "h-[calc(100vh-70px)]"
        } w-fit gap-2 pr-2 pt-3`}
      >
        {show3DControls && !showContModification ? (
          <div className="bounty-scrollbar overflow-y-auto [&>div]:w-[174px]">
            <URR
              reset={reset}
              redo={redo}
              reflectToChangelog={reflectToChangelog}
              iprOverLays={iprOverLays}
              setIprOverlays={setIprOverlays}
              GLBData={GLBData}
              setAutoSave={setAutoSave}
              clearAutoSave={clearAutoSave}
            />
            <SidePanel3DControls
              setAutoSave={setAutoSave}
              dentalNotation={dentalNotation}
              maxStepNumber={maxStepNumber}
              currentStep={currentStep}
              tpLoaded={tpLoaded}
              GLBData={GLBData}
              iprOverLays={iprOverLays}
              setIprOverlays={setIprOverlays}
              movementTable={movementTable}
              parsedMovementTables={parsedMovementTables}
            />
            {/* <IPRSelector
              setAutoSave={setAutoSave}
              dentalNotation={dentalNotation}
              iprOverLays={iprOverLays}
              setIprOverlays={setIprOverlays}
              maxStepNumber={maxStepNumber}
            /> */}
          </div>
        ) : (
          <></>
        )}
        <div
          className="w-[260px]"
          onPointerEnter={() => {
            setLayout("isUI", true);
          }}
          onMouseLeave={() => {
            setLayout("isUI", false);
          }}
        >
          {show3DControls && !showContModification ? (
            <ShowOriginal
              reset={reset}
              showOriginalRemovedHover={showOriginalRemovedHover}
              setToggle={setToggle}
              show3DControls={show3DControls}
              showContModification={showContModification}
            />
          ) : null}
          {showContModification ? (
            <ShowContModification
              setToggle={setToggle}
              show3DControls={show3DControls}
              showContModification={showContModification}
            />
          ) : null}
          <ReviseTreatment
            reset={reset}
            clearAutoSave={clearAutoSave}
            isViewerControlsEnabled={isViewerControlsEnabled}
            startTracking={startTracking}
            TPdata={data}
            setAutoSave={setAutoSave}
            iprOverLays={iprOverLays}
            setIprOverlays={setIprOverlays}
            maxStepNumber={maxStepNumber}
          />
        </div>
      </div>
    ) : (
      <></>
    );
  },
);

export default TeethControls;
